import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import {
  Box,
  InputAdornment,
  IconButton
} from "@mui/material";
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff
} from "@mui/icons-material";
import { NavLink, useNavigate } from "react-router-dom";
import Microsofticon from "../../assets/images/microsoft_732221.png";
import API_BASE_URL from "../../components/config/appConfig";
import { loginSchema } from "../../Validations/schema";
import { jwtDecode } from "jwt-decode";
import { toLowerCaseEmail } from "../../utilities/email";
import { useToast } from "../../components/Toast";
import { AuthBoardArt } from "./authArt";
import { IconBoard, IconChart, IconShield } from "./landingArt";
import {
  AuthLayout,
  AuthNotice,
  AuthTextField,
  AuthPrimaryButton,
  AuthSocialButton,
  AuthError,
  AuthRise,
  BRAND_DEEP,
  EASE
} from "./authUi";
import "./home.scss";

const STAGE_POINTS = [
  { icon: IconBoard, label: "Sprint boards" },
  { icon: IconChart, label: "Live reports" },
  { icon: IconShield, label: "Secure access" },
];

const Login = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (values) => {
    const loginData = {
      email: toLowerCaseEmail(values.email),
      password: values.password,
    };

    try {
      const response = await fetch(`${API_BASE_URL}/users/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(loginData),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("accessToken", data.accessToken);
        toast.success("Signed in successfully");
        if (data.email === "sushmitha.gopari@motivitylabs.com") {
          navigate("/admin/dashboard");
        } else {
          navigate("/projects");
        }
      } else {
        const responsess = await response.json();
        const errorMessage = await responsess.message;
        setError(errorMessage);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("An error occurred. Please try again later.");
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("accessToken");

    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        if (decodedToken.email === "sushmitha.gopari@motivitylabs.com") {
          navigate("/admin/dashboard");
        } else {
          navigate("/projects");
        }
      } catch (error) {
        console.error("Invalid token or token decoding failed", error);
      }
    }
  }, [navigate]);

  const notice = (
    <AuthNotice open={snackbarOpen} onClose={handleSnackbarClose}>
      {error}
    </AuthNotice>
  );

  return (
    <AuthLayout
      notice={notice}
      title={<>Plan the sprint.<br /><em>Ship the work.</em></>}
      lead="Boards, backlogs and burn-downs in one place — so your team always knows what is next."
      points={STAGE_POINTS}
      art={<AuthBoardArt />}
    >
      <h2 className="au-title au-rise" style={{ '--au-d': '.06s' }}>
        Welcome back
      </h2>
      <p className="au-sub au-rise" style={{ '--au-d': '.12s' }}>
        Login to continue to your account
      </p>

      <Formik
        initialValues={{
          email: "",
          password: "",
        }}
        validationSchema={loginSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched }) => (
          <Form>
            <AuthRise delay={0.18} mb={2.5}>
              <Field name="email">
                {({ field }) => (
                  <AuthTextField
                    {...field}
                    label="Email Address"
                    variant="outlined"
                    fullWidth
                    placeholder="Enter your email address"
                    error={touched.email && !!errors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </Field>
              <AuthError name="email" />
            </AuthRise>

            <AuthRise delay={0.24} mb={3.5}>
              <Field name="password">
                {({ field }) => (
                  <AuthTextField
                    {...field}
                    label="Password"
                    variant="outlined"
                    fullWidth
                    placeholder="Enter your password"
                    type={showPassword ? "text" : "password"}
                    error={touched.password && !!errors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleTogglePasswordVisibility}
                            edge="end"
                            sx={{
                              color: '#98a0b0',
                              transition: `color .25s ${EASE}`,
                              '&:hover': {
                                color: BRAND_DEEP,
                                background: 'rgba(0, 172, 86, 0.08)',
                              },
                            }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </Field>
              <AuthError name="password" />
            </AuthRise>

            <AuthRise delay={0.3}>
              <AuthPrimaryButton
                type="submit"
                variant="contained"
                fullWidth
                disabled={isSubmitting}
                size="large"
                disableElevation
              >
                {isSubmitting ? "Signing in..." : "Sign in"}
              </AuthPrimaryButton>
            </AuthRise>
          </Form>
        )}
      </Formik>

      <div className="au-links-row au-rise" style={{ '--au-d': '.36s' }}>
        <NavLink className="au-link" to="/register">
          Create account
        </NavLink>
        <NavLink className="au-link" to="/forgot-password">
          Forgot password?
        </NavLink>
      </div>

      <div className="au-divider au-rise" style={{ '--au-d': '.42s' }}>
        <span>OR</span>
      </div>

      <AuthRise delay={0.48}>
        <AuthSocialButton variant="outlined" fullWidth size="large">
          <img src={Microsofticon} alt="Microsoft icon" />
          Continue with Microsoft
        </AuthSocialButton>
      </AuthRise>
    </AuthLayout>
  );
};

export default Login;
